import React, {ReactElement} from 'react';
import styled from 'styled-components';

import {IconName} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {BoxedIcon} from '../../boxedIcon';
import {Skeleton} from '../../skeleton';
import {
  getCellBackgroundColor,
  getCellIndexStyles,
  getCellTextColor,
  getTableCellAvatarSize,
} from '../helpers/tableCellHelpers';
import {getWidthSettingsStyles} from '../tableColumn';
import {TableCell, TableCellProps, TableCellStyleProps} from './tableCell';

/*
 * Props.
 */

export interface IconCellProps<S> extends TableCellProps<S> {
  iconName?: IconName;
  iconSize?: VisualSizesEnum;
  iconColor?: string;
  backgroundColor?: string;
  borderColor?: string;
  useNormalFontWeight?: boolean;
}

/*
 * Styles.
 */

const StyledIconCellContainerDiv = styled.div<TableCellStyleProps>`
  padding: 0 12px;
  overflow: hidden;
  white-space: nowrap;
  display: flex;
  flex-flow: row;
  height: 100%;
  align-items: center;

  ${(p) => getCellBackgroundColor(p.$isHovering, p.$isSelected)}
  ${(p) => getWidthSettingsStyles(p.$settings?.cellWidth, p.$index)}
  ${(p) =>
    getCellIndexStyles(
      p.$index,
      p.$isSelected,
      p.$shouldRemoveTopBorderRadius,
      p.$shouldRemoveBottomBorderRadius,
    )}
`;

interface StyledIconDivProps {
  $iconSize: number;
}

const StyledIconContainerDiv = styled.div<StyledIconDivProps>`
  flex: 0 0 ${(p) => p.$iconSize}px;
  margin-right: 12px;
`;

interface StyledLabelDivProps {
  $isSelected: boolean;
  $useNormalFontWeight: boolean;
}

const StyledLabelDiv = styled.div<StyledLabelDivProps>`
  align-items: center;
  flex: 1;
  font-size: ${fontSizes.medium};
  font-weight: ${(p) => (p.$useNormalFontWeight ? fontWeights.normal : fontWeights.bold)};

  ${ellipsis()};
  ${(p) => getCellTextColor(p.$isSelected)}
`;

/*
 * Component.
 */

export const IconCell = <T = string,>(props: IconCellProps<T>): ReactElement => {
  const theme = useUserTheme();
  const {
    isSelected,
    isHovering,
    isLoading,
    size,
    settings,
    index,
    iconSize,
    iconName,
    backgroundColor = theme.greys.white,
    iconColor = theme.greys.shade70,
    borderColor = theme.greys.shade30,
    shouldShowStickyGradient,
    shouldRemoveTopBorderRadius,
    shouldRemoveBottomBorderRadius,
    useNormalFontWeight,
  } = props;

  const iconContainerSizePx = getTableCellAvatarSize(size, iconSize);

  return (
    <TableCell<T>
      isSelected={isSelected}
      isHovering={isHovering}
      isLoading={isLoading}
      size={size}
      settings={settings}
      index={index}
      value={props.value}
      shouldShowStickyGradient={shouldShowStickyGradient}
      shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
      shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
      renderLoading={() => (
        <StyledIconCellContainerDiv
          $settings={settings}
          $isHovering={false}
          $isSelected={Boolean(isSelected)}
          $index={index}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledIconContainerDiv $iconSize={iconContainerSizePx}>
            <Skeleton
              height={`${iconContainerSizePx}px`}
              width={`${iconContainerSizePx}px`}
              borderRadius="50%"
              scheme={settings?.loading?.style}
            />
          </StyledIconContainerDiv>
          <StyledLabelDiv $isSelected={false} $useNormalFontWeight={useNormalFontWeight ?? false}>
            <Skeleton height="16px" width="140px" borderRadius="4px" scheme={settings?.loading?.style} />
          </StyledLabelDiv>
        </StyledIconCellContainerDiv>
      )}
      renderCell={(value) => (
        <StyledIconCellContainerDiv
          $settings={settings}
          $isHovering={Boolean(isHovering)}
          $index={index}
          $isSelected={Boolean(isSelected)}
          $shouldRemoveTopBorderRadius={shouldRemoveTopBorderRadius}
          $shouldRemoveBottomBorderRadius={shouldRemoveBottomBorderRadius}
        >
          <StyledIconContainerDiv $iconSize={iconContainerSizePx}>
            <BoxedIcon
              name={iconName || 'content'}
              backgroundColor={backgroundColor}
              color={iconColor}
              borderColor={borderColor}
              borderStyle="solid"
              size={iconSize}
            />
          </StyledIconContainerDiv>
          <StyledLabelDiv
            $isSelected={Boolean(isSelected)}
            $useNormalFontWeight={useNormalFontWeight ?? false}
          >
            {value}
          </StyledLabelDiv>
        </StyledIconCellContainerDiv>
      )}
    />
  );
};
